import React, { useState } from "react"

import { NotificationManager } from "react-notifications"

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useTheme,
} from "@chakra-ui/core"

import { gql, useClient, useMutation } from "hooks/urql"

import Avatar from "components/Messaging/Avatar"
import RemoveButton from "components/RemoveButton"

export type User = {
  messagingDisplayName: string
  id?: string
  avatar: {
    thumbUrl: string
  }
}

export type TutorAvatarsProps = {
  viewerId: string
  messageThread: {
    id: string
    author: User
    users: User[]
  }
}

const TutorAvatars: React.FC<TutorAvatarsProps> = ({
  viewerId,
  messageThread,
}) => {
  const client = useClient()
  const theme: any = useTheme()
  const [activeUser, setActiveUser] = useState(null)
  const [, removeMutation]: any[] = useMutation(removeUserMutation, client)

  const removeUser = () => {
    removeMutation({ messageThreadId: messageThread.id, userId: activeUser.id })
      .then(
        result => {
          const { failures } = result.data.messageThreadRemoveUser
          if (failures.length > 0) {
            NotificationManager.error(
              `Failed to remove user: ${failures[0]["message"]}`
            )
          } else {
            NotificationManager.info("User removed")
          }
        },
        () => NotificationManager("Something went wrong")
      )
      .catch(() => NotificationManager("Something went wrong"))
      .finally(() => setActiveUser(null))
  }

  return (
    <div className="flex space-x-1">
      <Modal isOpen={!!activeUser}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove Tutor</ModalHeader>
          <ModalBody>
            <p className="mb-3">
              Are you sure? {activeUser?.messagingDisplayName} will no longer
              have access to any of the messages in this thread.
            </p>
          </ModalBody>
          <ModalFooter>
            <div className="flex w-full justify-between">
              <Button
                {...theme.buttonVariants.view}
                onClick={() => setActiveUser(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={removeUser}
                {...theme.buttonVariants.cloudroom}
              >
                Submit
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="isolate flex space-x-2 overflow-hidden">
        {messageThread.users.map(user => (
          <span
            className="relative mx-1 max-w-[96px] overflow-hidden overflow-ellipsis pt-1 pr-1 text-center"
            key={user.id}
          >
            {viewerId === messageThread.author.id && user.id !== viewerId && (
              <div className="absolute -top-1 -right-4 z-10">
                <RemoveButton onClick={() => setActiveUser(user)} />
              </div>
            )}
            <div className="flex flex-col items-center space-y-1">
              <Avatar
                size="medium"
                src={user.avatar.thumbUrl}
                alt={user.messagingDisplayName}
              />
              <div>
                {user.id === viewerId ? "You" : user.messagingDisplayName}
              </div>
            </div>
          </span>
        ))}
      </div>
    </div>
  )
}

const removeUserMutation = gql`
  mutation RemoveTutor($messageThreadId: ID!, $userId: ID!) {
    messageThreadRemoveUser(
      messageThreadId: $messageThreadId
      userId: $userId
    ) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`
export default TutorAvatars
